import { Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { Router } from '@angular/router';
import CookieService from './cookie.service';
import { AppSettingService } from '../app.service';


export interface IUserOtherAccount {
	PersonId: string,
	Zone: string,
	Role: string
}

export interface IUserProfile {
	PersonId: string,
	PersonGuid: string,
	LastName: string,
	FirstName: string,
	MI: string,
	FullName: string,
	BirthDate: string,
	Email: string,
	APIToken: string,
	HighestRole: string,
	PersonRoles: { Role: string; },
	ZipCode: string,
	APIKey: string,
	AllowSignup: boolean,
	ZoneClient: string,
	ZoneDisplayName: string,
	IsJAZone: boolean,
	IsPublicZone: boolean,
	ManualUpdate: boolean,
	NeedGlobalAccount: boolean,
	RequireGlobalAccountCount: number,
	SessionTimestamp: string,
	OtherAccounts: IUserOtherAccount[],
	AllowedWidgets: string[],
	HomePage: string
}

@Injectable({
	providedIn: 'root'
})
export class UserInfoService {
	UserInfoTimestamp: any = null;

	constructor(
		private http: HttpClient,
		private router: Router
	) {
		this.UserInfoTimestamp = new Rx.BehaviorSubject(this.getProfile().SessionTimestamp);
		window.setInterval(() => {
			try {
				if (CookieService.getCookie("account")) {
					let acc: IUserProfile = CookieService.getCookie("account");
					if (acc.SessionTimestamp !== this.UserInfoTimestamp.getValue()) {
						console.log("account cookie change.. old:" + this.UserInfoTimestamp.getValue() + "    new:" + this.getProfile().SessionTimestamp);
						this.UserInfoTimestamp.next(this.getProfile().SessionTimestamp);
					}
				}
			}
			catch (ex) {
				console.log(ex);
			}
		}, 1000); // run every 1 second
	}

	private anonymousProfile: IUserProfile = {
		PersonId: "0",
		PersonGuid: "",
		LastName: "",
		FirstName: "",
		MI: "",
		FullName: "",
		BirthDate: "",
		Email: "",
		APIToken: "",
		HighestRole: "anonymous",
		PersonRoles: { Role: "anonymous" },
		ZipCode: "",
		APIKey: "",
		AllowSignup: false,
		ZoneClient: "JOURNEYS",
		ZoneDisplayName: "Global Account",
		IsJAZone: false,
		IsPublicZone: false,
		ManualUpdate: false,
		NeedGlobalAccount: false,
		RequireGlobalAccountCount: 0,
		SessionTimestamp: "",
		OtherAccounts: [],
		AllowedWidgets: ["Signup", "Login"],
		HomePage: AppSettingService.baseUrl + "/User/Login"
	}

	getProfile(): IUserProfile {
		let acc: IUserProfile = CookieService.getCookie("account");
		if (acc == null || acc.PersonId == null || acc.PersonGuid == null) {
			this.anonymousProfile.SessionTimestamp = new Date().toUTCString();
			return this.anonymousProfile;
		}
		else {
			return acc;
		}
	}

	clearStorage(): void {
		try { sessionStorage.clear(); } catch (ex) { ; }
		/* do not clean localStorage. It store the idle timeout */
	}

	private signOff(): Rx.Observable<any> {
		return this.http.get<any>(
			AppSettingService.appConfig.JourneyAPIUrl + "Session/SignOff",
			{
				withCredentials: true,
				headers: { 'Authorization': this.authHeader(false) }
			}
		);
	}

	clearProfile(goLogin: boolean): void {
		// info server to logoff
		this.signOff().subscribe({
			next: (response: any) => {
				this.clearStorage();
				if (goLogin) {
					window.location.href = AppSettingService.baseUrl + "/User/Login";
				}
			},
			error: (err: any) => {
				console.log(err);
				this.clearStorage();
				if (goLogin) {
					window.location.href = AppSettingService.baseUrl + "/User/Login";
				}
			}
		});
	}

	isLogin(): boolean {
		try {
			var s = this.getProfile();
			if (s != null && s.PersonId != null && s.PersonId != "" && s.PersonId != "0")
				return true;
			else
				return false;
		}
		catch (ex) {
			return false;
		}
	}

	isStudent(): boolean {
		try {
			var s = this.getProfile();
			if (s != null && s.HighestRole != null && s.HighestRole.toLowerCase() == "student")
				return true;
			else
				return false;
		}
		catch (ex) {
			return false;
		}
	}

	isAnonymous(): boolean {
		try {
			let anonymous = true;
			let s = this.getProfile();
			anonymous = (s.HighestRole == null || s.HighestRole.toLowerCase() == 'anonymous') ? true : false;
			return anonymous;
		}
		catch (ex) {
			return false;
		}
	}

	// force user to login again if no authentication or session expire
	enforceLogin(): void {
		if (!this.isLogin()) {
			this.router.navigateByUrl("/User/Login");
		}
	}

	watchSessionTimestamp(): Rx.Observable<string> {
		return Rx.of(this.getProfile().SessionTimestamp);
	}

	authHeader(openLogin: boolean = false): string {
		try {
			let s = this.getProfile();
			// check if need to openLogin window or not. Only open if the user currently is anonymous
			if (openLogin != null && openLogin == true && this.isLogin() && this.isAnonymous()) {
				// have Circular dependency issue, we cannot openlogin here, have to redirect user to login page
				this.router.navigateByUrl("/User/Login");
				return "Bearer ^^^xxxxxxxxxxxxxxxxxx^^^"; // return falut login info
			}
			if (s.APIToken == undefined) {
				return "";
			}
			else {
				return atob(s.APIToken);
			}
		}
		catch (ex) {
			return "";
		}
	}

	allowSignup(): boolean {
		try {
			var s = this.getProfile();
			return s.AllowSignup === true;
		}
		catch (ex) {
			return false;
		}
	}

	getHomePage(): string {
		return this.getProfile().HomePage;
	}

	getInstitutionCode(): string {
		let institutionCode = '';
		if (this.isAnonymous()) {
			let homepage: string = this.getProfile().HomePage;
			if (homepage.toLowerCase().indexOf("/collegescorecard/school/") > 0) {
				institutionCode = "IPEDS-" + homepage.split("/").pop();
			}
			else if (homepage.toLowerCase().indexOf("/nces/school/") > 0) {
				institutionCode = "NCES-" + homepage.split("/").pop();
			}
		}
		return institutionCode;
	}

	isSystemAdmin(): boolean {
		try {
			let _is = false;
			let s = this.getProfile();
			_is = (s.PersonId != null && s.PersonId === '-1') ? true : false;
			return _is;
		}
		catch (ex) {
			return false;
		}
	}

	getZoneName(): string {
		try {
			return this.getProfile().ZoneClient;
		}
		catch (ex) {
			return '';
		}
	}

	isPublicZone(): boolean {
		try {
			return this.getProfile().IsPublicZone;
		}

		catch (ex) {
			return false;
		}
	}

	isJAZone(): boolean {
		try {
			return this.getProfile().IsJAZone;
		}
		catch (ex) {
			return false;
		}
	}

	isZoneAdmin(): boolean {
		try {
			return this.getProfile().HighestRole == 'ZoneAdmin';
		}
		catch (ex) {
			return false;
		}
	}

	get anonymousEmail(): string {
		return sessionStorage.getItem('anonymousUser') ?? "";
	}
	set anonymousEmail(v: string) {
		if (v == null) {
			sessionStorage.removeItem("anonymousUser");
		}
		else {
			sessionStorage.setItem('anonymousUser', v);
		}
	}

	get Username(): string {
		if (this.isAnonymous()) {
			return "";
		}
		else {
			let auth: string = atob(this.getProfile().APIToken);
			let up: string = atob(auth.replace("Bearer ", "").replace("Basic ", "").trim());
			let u: string = up.indexOf("\t") > 0 ? up.split("\t")[0] : up.split(":")[0];
			return (u.indexOf("\\") > 0 ? u.split("\\")[1] : u).trim();
		}
	}
}
